import React, { useCallback, useState } from "react";

import { EmptyContent, JsonEditor } from "@/common";
import { useNotification } from "@/common/notifications";
import Button from "@imminently/decisively-ui/Button";
import Checkbox from "@imminently/decisively-ui/Checkbox";
import Detail from "@imminently/decisively-ui/Detail";
import DetailHorizontalList from "@imminently/decisively-ui/DetailHorizontalList";
import ItemList from "@imminently/decisively-ui/ItemList";
import Label from "@imminently/decisively-ui/Label";
import SplitPane from "@imminently/decisively-ui/SplitPane";
import Tabs, { type TabValue } from "@imminently/decisively-ui/Tabs";
import TextInput from "@imminently/decisively-ui/TextInput";
import { getSessionMappingOutput } from "@/app/providers/services";

export interface MappingTabProps {
  session: any;
  release: any;
  sessionId: string;
  versionId: string | undefined;
}

const MAPPING_TYPES = ["response", "delta"];

const MappingTab = ({ session, release, sessionId, versionId }: MappingTabProps) => {
  const notify = useNotification();
  const [selectedMapping, setSelectedMapping] = useState<any>(null);
  const [outputTab, setOutputTab] = useState<TabValue["value"]>("mapping-file");
  const [deltaDebugSubTab, setDeltaDebugSubTab] = useState<"previous" | "current" | "output">("output");
  const [mappingOutput, setMappingOutput] = useState<string>("");
  const [mappingResult, setMappingResult] = useState<any>(null);
  const [filterValue, setFilterValue] = useState("");
  const [includeDeltaDebug, setIncludeDeltaDebug] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const effectiveSessionId = versionId ? `${sessionId}-${versionId}` : sessionId;
  const mappings = React.useMemo(() => {
    const list = release?.mapping ?? [];
    return list.filter((m: any) => m.type && MAPPING_TYPES.includes(m.type));
  }, [release?.mapping]);

  const handleGenerate = useCallback(async () => {
    if (!selectedMapping?.id) {
      return;
    }
    setIsGenerating(true);
    setMappingOutput("");
    setMappingResult(null);
    try {
      const isDelta = selectedMapping?.type === "delta";
      const result = await getSessionMappingOutput(effectiveSessionId, selectedMapping.id, {
        filter: filterValue.trim() || undefined,
        includeDebug: isDelta && includeDeltaDebug,
      });
      setMappingResult(result);
      const hasDeltaDebug =
        result &&
        typeof result === "object" &&
        "output" in result &&
        "currentRecords" in result &&
        "previousRecords" in result;
      if (hasDeltaDebug) {
        setMappingOutput(JSON.stringify(result.output, null, 2));
        setDeltaDebugSubTab("output");
      } else {
        setMappingOutput(typeof result === "string" ? result : JSON.stringify(result, null, 2));
      }
      setOutputTab("output");
    } catch (error: any) {
      notify.error(error?.message ?? "Failed to run mapping");
    } finally {
      setIsGenerating(false);
    }
  }, [effectiveSessionId, selectedMapping?.id, selectedMapping?.type, filterValue, includeDeltaDebug, notify]);

  const outputTabs: TabValue[] = [
    {
      value: "mapping-file",
      label: "Mapping file",
      content: selectedMapping ? (
        <div className="flex flex-col flex-1 min-h-0 p-2">
          <JsonEditor
            className="flex-1 min-h-[200px]"
            value={JSON.stringify(selectedMapping, null, 2)}
            disabled={true}
            withCopy={true}
          />
        </div>
      ) : (
        <div className="p-4 text-muted-foreground">Select a mapping from the list.</div>
      ),
    },
    {
      value: "output",
      label: "Output",
      content: (
        <div className="flex flex-col flex-1 min-h-0 p-2 gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <TextInput
              value={filterValue}
              onValueChange={setFilterValue}
              placeholder="Context path filter (e.g. goal/1)"
              aria-label="Filter"
              className="flex-1 min-w-[200px]"
              fullWidth
            />
            {selectedMapping?.type === "delta" ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  value={includeDeltaDebug}
                  onValueChange={setIncludeDeltaDebug}
                  aria-label="Include debug (previous/current records)"
                />
                <Label>Include debug (previous/current records)</Label>
              </div>
            ) : null}
            <Button
              label={isGenerating ? "Generating…" : "Generate"}
              onClick={handleGenerate}
              disabled={!selectedMapping || isGenerating}
            >
              {isGenerating ? "Generating…" : "Generate"}
            </Button>
          </div>
          {mappingResult &&
          typeof mappingResult === "object" &&
          "output" in mappingResult &&
          "currentRecords" in mappingResult &&
          "previousRecords" in mappingResult ? (
            <Tabs
              value={deltaDebugSubTab}
              onValueChange={(v) => setDeltaDebugSubTab(v as "previous" | "current" | "output")}
              className="flex flex-col flex-1 min-h-0 overflow-hidden"
            >
              <Tabs.List className="flex-shrink-0 border-b border-border mb-2">
                <Tabs.Trigger value="previous">Previous</Tabs.Trigger>
                <Tabs.Trigger value="current">Current</Tabs.Trigger>
                <Tabs.Trigger value="output">Output</Tabs.Trigger>
              </Tabs.List>
              <Tabs.Content
                value="previous"
                className="flex-1 min-h-0 overflow-auto data-[state=active]:flex flex-col"
              >
                <JsonEditor
                  className="flex-1 min-h-[200px]"
                  value={JSON.stringify(mappingResult.previousRecords, null, 2)}
                  disabled={true}
                  withCopy={true}
                />
              </Tabs.Content>
              <Tabs.Content
                value="current"
                className="flex-1 min-h-0 overflow-auto data-[state=active]:flex flex-col"
              >
                <JsonEditor
                  className="flex-1 min-h-[200px]"
                  value={JSON.stringify(mappingResult.currentRecords, null, 2)}
                  disabled={true}
                  withCopy={true}
                />
              </Tabs.Content>
              <Tabs.Content
                value="output"
                className="flex-1 min-h-0 overflow-auto data-[state=active]:flex flex-col"
              >
                <JsonEditor
                  className="flex-1 min-h-[200px]"
                  value={mappingOutput}
                  disabled={true}
                  withCopy={true}
                />
              </Tabs.Content>
            </Tabs>
          ) : (
            <div className="flex-1 min-h-[200px]">
              <JsonEditor
                className="w-full h-full min-h-[200px]"
                value={mappingOutput}
                disabled={true}
                withCopy={true}
              />
            </div>
          )}
        </div>
      ),
    },
  ];

  if (!release) {
    return (
      <EmptyContent
        messages={["No release in scope"]}
        img=""
        className="w-full min-h-[200px]"
      />
    );
  }

  return (
    <SplitPane
      direction="horizontal"
      gutterSize={6}
    >
      <SplitPane.Pane
        minSize={180}
        preferredSize="260px"
      >
        <ItemList
          className="h-full"
          header="Mappings"
          items={mappings}
          getItemKey={(m: any, index) => m?.id || `mapping-${index}`}
          renderEmpty={() => (
            <div className="p-3 text-muted-foreground text-sm">No response or delta mappings in this release.</div>
          )}
          renderItem={(m: any) => (
              <ItemList.Item
                key={m.id}
                selected={selectedMapping?.id === m.id}
                onClick={() => setSelectedMapping(m)}
              >
                <ItemList.Left>
                  <ItemList.Title>{m.name ?? m.id}</ItemList.Title>
                  <div className="text-muted-foreground text-sm">({m.type})</div>
                </ItemList.Left>
              </ItemList.Item>
          )}
        />
      </SplitPane.Pane>
      <SplitPane.Pane>
        <div className="flex flex-col h-full min-h-0">
          {selectedMapping ? (
            <>
              <DetailHorizontalList className="py-3 border-b border-border flex-wrap gap-y-2">
                <DetailHorizontalList.Item>
                  <DetailHorizontalList.Title>Title</DetailHorizontalList.Title>
                  <DetailHorizontalList.Content>{selectedMapping.name ?? selectedMapping.id}</DetailHorizontalList.Content>
                </DetailHorizontalList.Item>
                <DetailHorizontalList.Item>
                  <DetailHorizontalList.Title>Type</DetailHorizontalList.Title>
                  <DetailHorizontalList.Content>{selectedMapping.type}</DetailHorizontalList.Content>
                </DetailHorizontalList.Item>
                {selectedMapping.created ? (
                  <DetailHorizontalList.Item>
                    <DetailHorizontalList.Title>Created</DetailHorizontalList.Title>
                    <DetailHorizontalList.Content>{selectedMapping.created}</DetailHorizontalList.Content>
                  </DetailHorizontalList.Item>
                ) : null}
                {selectedMapping.lastModified ? (
                  <DetailHorizontalList.Item>
                    <DetailHorizontalList.Title>Last modified</DetailHorizontalList.Title>
                    <DetailHorizontalList.Content>{selectedMapping.lastModified}</DetailHorizontalList.Content>
                  </DetailHorizontalList.Item>
                ) : null}
              </DetailHorizontalList>
              <Tabs
                value={outputTab}
                onValueChange={(v) => setOutputTab(v as TabValue["value"])}
                className="flex flex-col flex-1 overflow-hidden min-h-0"
              >
                <Tabs.List className="border-b border-border">
                  {outputTabs.map((tab) => (
                    <Tabs.Trigger
                      key={tab.value}
                      value={tab.value}
                    >
                      {tab.label}
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>
                {outputTabs.map((tab) => (
                  <Tabs.Content
                    key={tab.value}
                    value={tab.value}
                    className="flex-1 overflow-auto min-h-0 data-[state=active]:flex flex-col"
                  >
                    {tab.content}
                  </Tabs.Content>
                ))}
              </Tabs>
            </>
          ) : (
            <div className="flex items-center justify-center flex-1 text-muted-foreground p-4">
              Select a mapping from the list to view its definition and generate output.
            </div>
          )}
        </div>
      </SplitPane.Pane>
    </SplitPane>
  );
};

export default MappingTab;
